/**
 * 🌐 API Service - Frontend Integration
 * 
 * Cliente HTTP para conectar com nossa API REST completa
 * Integra com TanStack Query para cache e gerenciamento de estado
 * 
 * Endpoints disponíveis:
 * - /api/events     - Gerenciamento de eventos  
 * - /api/users      - Gerenciamento de usuários
 * - /api/participants - Gerenciamento de participantes
 * - /api/meetings   - Gerenciamento de reuniões
 */

import type { 
  APIResponse, 
  HealthCheckResponse 
} from '@shared/types/api';

import type { 
  Event, 
  User, 
  Participant, 
  Meeting,
  UserRole
} from '@shared/types';

// ========================================
// CONFIGURAÇÃO BASE
// ========================================

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

/**
 * Configuração base do cliente HTTP
 */
class ApiClient {
  private baseURL: string;
  private token: string | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    // Recuperar token do localStorage se disponível
    this.token = localStorage.getItem('auth_token');
  }

  /**
   * Define o token de autenticação
   */
  setToken(token: string | null) {
    this.token = token;
    if (token) {
      localStorage.setItem('auth_token', token);
    } else {
      localStorage.removeItem('auth_token');
    }
  }

  /**
   * Headers padrão para todas as requisições
   */
  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    console.log('🔍 [API] Current token:', this.token);
    
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
      console.log('🔍 [API] Authorization header set:', headers['Authorization']);
    } else {
      console.log('❌ [API] No token available!');
    }

    return headers;
  }

  /**
   * Método genérico para fazer requisições HTTP
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    
    const response = await fetch(url, {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  /**
   * GET request
   */
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

// Instância singleton do cliente API
export const apiClient = new ApiClient(API_BASE_URL);

// ========================================
// TIPOS ESPECÍFICOS DA API
// ========================================

export interface EventFilters {
  organizerId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}

export interface ParticipantFilters {
  eventId?: string;
  role?: UserRole;
  companyType?: string;
}

export interface UserFilters {
  role?: UserRole;
  companyType?: string;
  isActive?: boolean;
}

export interface MeetingFilters {
  eventId?: string;
  participantId?: string;
  status?: string;
  date?: string;
}

export interface CreateEventData {
  name: string;
  description?: string;
  startDate: string;
  endDate: string;
  location?: string;
  organizerId: string;
  maxParticipants?: number;
}

export interface UpdateEventData extends Partial<CreateEventData> {}

export interface CreateUserData {
  name: string;
  email: string;
  role: UserRole;
  companyName?: string;
  companyType?: string;
}

export interface UpdateUserData extends Partial<CreateUserData> {}

export interface CreateParticipantData {
  name: string;
  email: string;
  companyName: string;
  companyType: string;
  role: UserRole;
  eventId: string;
}

export interface UpdateParticipantData extends Partial<CreateParticipantData> {}

export interface CreateMeetingData {
  title: string;
  description?: string;
  scheduledDate: string;
  duration: number;
  eventId: string;
  participantIds: string[];
}

export interface UpdateMeetingData extends Partial<CreateMeetingData> {}

// ========================================
// SERVIÇOS DE API POR ENTIDADE
// ========================================

/**
 * 🎯 EVENTS API
 */
export const eventsApi = {
  /**
   * Listar todos os eventos
   */
  getAll: (filters?: EventFilters): Promise<APIResponse<Event[]>> => {
    const params = new URLSearchParams();
    if (filters?.organizerId) params.append('organizerId', filters.organizerId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    
    const query = params.toString();
    return apiClient.get(`/api/events${query ? `?${query}` : ''}`);
  },

  /**
   * Buscar evento por ID
   */
  getById: (id: string): Promise<APIResponse<Event>> => {
    return apiClient.get(`/api/events/${id}`);
  },

  /**
   * Criar novo evento
   */
  create: (data: CreateEventData): Promise<APIResponse<Event>> => {
    return apiClient.post('/api/events', data);
  },

  /**
   * Atualizar evento
   */
  update: (id: string, data: UpdateEventData): Promise<APIResponse<Event>> => {
    return apiClient.put(`/api/events/${id}`, data);
  },

  /**
   * Deletar evento
   */
  delete: (id: string): Promise<APIResponse<null>> => {
    return apiClient.delete(`/api/events/${id}`);
  },

  /**
   * Obter estatísticas do evento
   */
  getStats: (id: string): Promise<APIResponse<any>> => {
    return apiClient.get(`/api/events/${id}/stats`);
  },
};

/**
 * 👥 USERS API
 */
export const usersApi = {
  /**
   * Listar todos os usuários
   */
  getAll: (filters?: UserFilters): Promise<APIResponse<User[]>> => {
    const params = new URLSearchParams();
    if (filters?.role) params.append('role', filters.role);
    if (filters?.companyType) params.append('companyType', filters.companyType);
    if (filters?.isActive !== undefined) params.append('isActive', filters.isActive.toString());
    
    const query = params.toString();
    return apiClient.get(`/api/users${query ? `?${query}` : ''}`);
  },

  /**
   * Buscar usuário por ID
   */
  getById: (id: string): Promise<APIResponse<User>> => {
    return apiClient.get(`/api/users/${id}`);
  },

  /**
   * Criar novo usuário
   */
  create: (data: CreateUserData): Promise<APIResponse<User>> => {
    return apiClient.post('/api/users', data);
  },

  /**
   * Atualizar usuário
   */
  update: (id: string, data: UpdateUserData): Promise<APIResponse<User>> => {
    return apiClient.put(`/api/users/${id}`, data);
  },

  /**
   * Deletar usuário
   */
  delete: (id: string): Promise<APIResponse<null>> => {
    return apiClient.delete(`/api/users/${id}`);
  },

  /**
   * Obter perfil do usuário atual
   */
  getProfile: (): Promise<APIResponse<User>> => {
    return apiClient.get('/api/users/profile');
  },

  /**
   * Atualizar perfil do usuário atual
   */
  updateProfile: (data: UpdateUserData): Promise<APIResponse<User>> => {
    return apiClient.put('/api/users/profile', data);
  },
};

/**
 * 🏢 PARTICIPANTS API
 */
export const participantsApi = {
  /**
   * Listar todos os participantes
   */
  getAll: (filters?: ParticipantFilters): Promise<APIResponse<Participant[]>> => {
    const params = new URLSearchParams();
    if (filters?.eventId) params.append('eventId', filters.eventId);
    if (filters?.role) params.append('role', filters.role);
    if (filters?.companyType) params.append('companyType', filters.companyType);
    
    const query = params.toString();
    return apiClient.get(`/api/participants${query ? `?${query}` : ''}`);
  },

  /**
   * Buscar participante por ID
   */
  getById: (id: string): Promise<APIResponse<Participant>> => {
    return apiClient.get(`/api/participants/${id}`);
  },

  /**
   * Criar novo participante
   */
  create: (data: CreateParticipantData): Promise<APIResponse<Participant>> => {
    return apiClient.post('/api/participants', data);
  },

  /**
   * Atualizar participante
   */
  update: (id: string, data: UpdateParticipantData): Promise<APIResponse<Participant>> => {
    return apiClient.put(`/api/participants/${id}`, data);
  },

  /**
   * Deletar participante
   */
  delete: (id: string): Promise<APIResponse<null>> => {
    return apiClient.delete(`/api/participants/${id}`);
  },

  /**
   * Adicionar representante ao participante
   */
  addRepresentative: (id: string, representativeData: any): Promise<APIResponse<Participant>> => {
    return apiClient.post(`/api/participants/${id}/representative`, representativeData);
  },
};

/**
 * 🤝 MEETINGS API
 */
export const meetingsApi = {
  /**
   * Listar todas as reuniões
   */
  getAll: (filters?: MeetingFilters): Promise<APIResponse<Meeting[]>> => {
    const params = new URLSearchParams();
    if (filters?.eventId) params.append('eventId', filters.eventId);
    if (filters?.participantId) params.append('participantId', filters.participantId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.date) params.append('date', filters.date);
    
    const query = params.toString();
    return apiClient.get(`/api/meetings${query ? `?${query}` : ''}`);
  },

  /**
   * Buscar reunião por ID
   */
  getById: (id: string): Promise<APIResponse<Meeting>> => {
    return apiClient.get(`/api/meetings/${id}`);
  },

  /**
   * Criar nova reunião
   */
  create: (data: CreateMeetingData): Promise<APIResponse<Meeting>> => {
    return apiClient.post('/api/meetings', data);
  },

  /**
   * Atualizar reunião
   */
  update: (id: string, data: UpdateMeetingData): Promise<APIResponse<Meeting>> => {
    return apiClient.put(`/api/meetings/${id}`, data);
  },

  /**
   * Deletar reunião
   */
  delete: (id: string): Promise<APIResponse<null>> => {
    return apiClient.delete(`/api/meetings/${id}`);
  },

  /**
   * Confirmar participação na reunião
   */
  confirm: (id: string): Promise<APIResponse<Meeting>> => {
    return apiClient.post(`/api/meetings/${id}/confirm`);
  },

  /**
   * Cancelar reunião
   */
  cancel: (id: string): Promise<APIResponse<Meeting>> => {
    return apiClient.post(`/api/meetings/${id}/cancel`);
  },
};

// ========================================
// SERVIÇOS AUXILIARES
// ========================================

/**
 * 🩺 HEALTH & INFO API
 */
export const systemApi = {
  /**
   * Health check
   */
  health: (): Promise<HealthCheckResponse> => {
    return apiClient.get('/health');
  },

  /**
   * Informações da API
   */
  info: (): Promise<APIResponse<any>> => {
    return apiClient.get('/api/info');
  },
};

/**
 * 🔐 AUTH UTILITIES
 */
export const authUtils = {
  /**
   * Fazer login (define o token)
   */
  login: (token: string) => {
    apiClient.setToken(token);
  },

  /**
   * Fazer logout (remove o token)
   */
  logout: () => {
    apiClient.setToken(null);
  },

  /**
   * Verificar se está logado
   */
  isAuthenticated: (): boolean => {
    return apiClient['token'] !== null;
  },

  /**
   * Obter token atual
   */
  getToken: (): string | null => {
    return apiClient['token'];
  },
};

// ========================================
// EXPORT DEFAULT
// ========================================

export default {
  events: eventsApi,
  users: usersApi,
  participants: participantsApi,
  meetings: meetingsApi,
  system: systemApi,
  auth: authUtils,
  client: apiClient,
};